/* eslint-disable */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal, Skeleton } from "@aqar-app/react-ui";
import { usePath } from "@/contexts/path/path";
import { InitialResolver } from "@/lib/use.middlewares";
import { AuthResolver } from "@/lib/serverOnly";
import { Filter } from "@/lib/path-resolver-middlewares/filters";
import { Category } from "@/lib/path-resolver-middlewares/category";
import { City } from "@/src/lib/path-resolver-middlewares/city";
import { Direction } from "@/lib/path-resolver-middlewares/direction";
import { District } from "@/lib/path-resolver-middlewares/district";
import { Filters } from "@/components/filters/filters";
import css from "./.module.scss";
import useFindListings from "../../components/new-resolver/useFindListings";
import ListingCard, { Listing } from "../../components/Listing/ListingCard";
import { CategoryList } from "@/components/new-resolver/CountersButtons";
import Title from "../../components/Title";
import AppLayout from "../../containers/layouts/AppLayout";
import { FloatingListingDetails } from "@/components/Listing/ListingScreen/floating.map";
import {
  createHtmlCityMarker,
  createHtmlListingMarker,
  Map,
  restriction,
  useBounds,
} from "@/components/map/map";
import { cityGeolocations, saudiPolygon } from "@/common/cityGeolocations";
import logger from "@/console";

import { List, MapControls } from "@/src/components/map/screen.components";
import WithAuthentication from "@/src/hoc/withAuthentication";
import {
  trackGenericCTAClickEvent,
  trackListingsList,
} from "@/lib/tracking/trackNavigationEvents";
import {
  listingToEECItemObject,
  trackSelectItem,
  trackViewItemList,
  trackViewProperties,
} from "@/lib/tracking/trackEECEvents";
import { financialFormat } from "@/lib/helpers";
import {
  trackAuctionPinClicked,
  trackMapAuctionListingClicked,
} from "@/lib/tracking/trackAuctionListingEvents";

const LISTINGS_SIZE = 50;
function getPrice(listing, filters) {
  const {
    price: p,
    price_12_payments,
    price_4_payments,
    price_2_payments,
  } = listing || {};

  let price = financialFormat(p);
  if (filters?.price_12_payments && price_12_payments)
    price = financialFormat(Math.round(price_12_payments / 12));
  if (filters?.price_4_payments && price_4_payments)
    price = financialFormat(price_4_payments);
  if (filters?.price_2_payments && price_2_payments)
    price = financialFormat(price_2_payments);
  return price;
}
const citiesIds = new Array(90).fill(0).map((_, i) => i + 1);
const defaultProps = {
  center: {
    // riyadh
    lat: 24.7136,
    lng: 46.6753,
  },
  zoom: 11,
};
export const FrozenMapOptions = {
  isFractionalZoomEnabled: false,
  draggable: false,
  scrollwheel: false,
  restriction: null,
  disableDoubleClickZoom: false,
};
export const FreeMapOptions = {
  isFractionalZoomEnabled: false,
  maxZoom: 0,
  draggable: true,
  restriction: null,
  scrollwheel: true,
  disableDoubleClickZoom: true,
};

function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function getMapCenterAndRadius(map) {
  if (!map) return {};
  const center = map.getCenter();
  const bounds = map.getBounds();
  const ne = bounds.getNorthEast();

  const radius = calculateDistance(
    center.lat(),
    center.lng(),
    ne.lat(),
    ne.lng(),
  );

  return {
    map_center: { lat: center.lat(), lng: center.lng() },
    map_radius_km: radius,
  };
}

/**
 * Calculate the intersection between a polygon and map bounds (rectangle)
 * Uses Sutherland-Hodgman algorithm for polygon clipping
 */
function getPolygonIntersection(polygonCoords, bounds) {
  if (!polygonCoords || polygonCoords.length === 0 || !bounds) {
    return null;
  }

  const { north, south, east, west } = bounds;

  // Convert polygon to simple points array
  let outputPolygon = polygonCoords.map((p) => ({ x: p.lng, y: p.lat }));

  // Clip against each edge of the bounding box
  // Left edge (x = west)
  outputPolygon = clipPolygonByEdge(outputPolygon, west, "left");
  if (outputPolygon.length === 0) return null;

  // Right edge (x = east)
  outputPolygon = clipPolygonByEdge(outputPolygon, east, "right");
  if (outputPolygon.length === 0) return null;

  // Bottom edge (y = south)
  outputPolygon = clipPolygonByEdge(outputPolygon, south, "bottom");
  if (outputPolygon.length === 0) return null;

  // Top edge (y = north)
  outputPolygon = clipPolygonByEdge(outputPolygon, north, "top");
  if (outputPolygon.length === 0) return null;

  // Convert back to lat/lng format
  return outputPolygon.map((p) => ({ lat: p.y, lng: p.x }));
}

function clipPolygonByEdge(polygon, edgeValue, edgeType) {
  if (polygon.length === 0) return [];

  const output = [];

  for (let i = 0; i < polygon.length; i++) {
    const current = polygon[i];
    const next = polygon[(i + 1) % polygon.length];

    const currentInside = isInsideEdge(current, edgeValue, edgeType);
    const nextInside = isInsideEdge(next, edgeValue, edgeType);

    if (currentInside && nextInside) {
      // Both inside: add next point
      output.push(next);
    } else if (currentInside && !nextInside) {
      // Leaving: add intersection point
      const intersection = getEdgeIntersection(
        current,
        next,
        edgeValue,
        edgeType,
      );
      if (intersection) output.push(intersection);
    } else if (!currentInside && nextInside) {
      // Entering: add intersection and next point
      const intersection = getEdgeIntersection(
        current,
        next,
        edgeValue,
        edgeType,
      );
      if (intersection) output.push(intersection);
      output.push(next);
    }
    // Both outside: add nothing
  }

  return output;
}

function isInsideEdge(point, edgeValue, edgeType) {
  switch (edgeType) {
    case "left":
      return point.x >= edgeValue;
    case "right":
      return point.x <= edgeValue;
    case "bottom":
      return point.y >= edgeValue;
    case "top":
      return point.y <= edgeValue;
    default:
      return false;
  }
}

function getEdgeIntersection(p1, p2, edgeValue, edgeType) {
  if (edgeType === "left" || edgeType === "right") {
    // Vertical edge
    const t = (edgeValue - p1.x) / (p2.x - p1.x);
    if (t >= 0 && t <= 1) {
      return {
        x: edgeValue,
        y: p1.y + t * (p2.y - p1.y),
      };
    }
  } else {
    // Horizontal edge
    const t = (edgeValue - p1.y) / (p2.y - p1.y);
    if (t >= 0 && t <= 1) {
      return {
        x: p1.x + t * (p2.x - p1.x),
        y: edgeValue,
      };
    }
  }
  return null;
}

function getLineIntersection(p1, p2, edge) {
  const x1 = p1.x,
    y1 = p1.y;
  const x2 = p2.x,
    y2 = p2.y;
  const x3 = edge.x1,
    y3 = edge.y1;
  const x4 = edge.x2,
    y4 = edge.y2;

  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-10) return null; // Lines are parallel

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;

  return {
    x: x1 + t * (x2 - x1),
    y: y1 + t * (y2 - y1),
  };
}

export function MapScreen() {
  const { updateContext, categories, categoriesDictionary, ...path } =
    usePath();
  const {
    listing,
    itemListName,
    listingType,
    category,
    district,
    city,
    direction,
    filters,
    previousPath
  } = path;
  const markersDictionary = useRef({});
  // Track if the current polygon was drawn by the user
  const userDrawnPolygonRef = useRef(false);
  // Store the original user-drawn polygon coordinates
  const originalDrawnPolygonRef = useRef(null);
  const citiesMarker = useMemo(() => {
    if (typeof window === "undefined") return [];
    return cityGeolocations.map((city, id) => ({
      html: createHtmlCityMarker({
        text: city.name.ar,
        className: `${css.marker} ${css.city}`,
      }),
      city,
      id: `${id}city`,
    }));
  }, []);

  const previousListings = useRef([]);
  const [map, setMap] = useState();
  const [l, { sov, loading, total, fetchMore }] = useFindListings(
    {
      size: LISTINGS_SIZE,
    },
    () => {
      if (!map || path.basePath !== "/map") return;
      trackViewProperties({
        listingType: category?.ga_listing_type || path.listingType,
        viewType: "map",
      });
    },
  );

  const listings = useMemo(
    () => [...new Set([...(l || []), ...(sov?.listings || [])])],
    [l, sov],
  );

  const [mode, setMode] = useState("DEFAULT" /*DRAWING|FLOATING_LIST*/);
  const [mapType, setMapType] = useState("roadmap");
  const bounds = useBounds();
  const activeBoundsRef = useRef();
  const [currentListing, setCurrentListing] = useState();
  const [fullListing, setFullListing] = useState();
  const listingDetailsRef = useRef();
  const [cityZoomLevel, setCityZoomLevel] = useState(false);

  // Track mode changes to reset the user-drawn polygon flag
  useEffect(() => {
    if (mode !== "DRAWING") {
      // Reset the flag when exiting drawing mode
      userDrawnPolygonRef.current = false;
    }
  }, [mode]);

  const allCitiesBounds = useMemo(() => {
    if (
      typeof window === "undefined" ||
      typeof google === "undefined" ||
      typeof window.google.maps.LatLngBounds === "undefined"
    )
      return undefined;
    const bounds = new window.google.maps.LatLngBounds();
    for (const { lat, lng } of cityGeolocations) {
      bounds.extend({ lat, lng });
    }
    return bounds;
  }, [map]);

  const filtersRef = useRef({});
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const citiesPolygon = useRef({});
  useEffect(() => {
    if (!map) return;
    if (typeof window === "undefined" || typeof google === "undefined") return;

    fetch("/next-api/cities/polygons")
      .then((res) => res.json())
      .then(({ data = [] } = {}) => {
        for (const { id, p } of data) {
          const bounds = new window.google.maps.LatLngBounds();
          for (const [lng, lat] of p) {
            bounds.extend({ lat, lng });
          }
          citiesPolygon.current[id] = bounds;
        }
      });
  }, [map]);
  const saudiBounds = useMemo(() => {
    if (
      typeof window === "undefined" ||
      typeof google === "undefined" ||
      typeof window.google.maps.LatLngBounds === "undefined"
    )
      return undefined;
    const bounds = new window.google.maps.LatLngBounds();
    for (const [lat, lng] of saudiPolygon) {
      bounds.extend({ lat, lng });
    }
    return bounds;
  }, [map]);

  const zoomRef = useRef({
    inProgress: false,
    value: 0,
    target: 0,
    direction: "in",
  });

  //useResolvePathForBackAndForward();

  const travelToBounds = (_bounds) => {
    const bounds = new window.google.maps.LatLngBounds(
      _bounds.southWest,
      _bounds.northEast,
    );
    activeBoundsRef.current = bounds;
    map.fitBounds(bounds, 0);
    map.setZoom(11);
  };

  const onMapChange = useCallback(() => {
    /* abort fetch listings if map is not ready
     *  or drawing mode or the zoom<9 [cities markers mode]
     * */
    if (zoomRef.current.inProgress) {
      zoomRef.current.inProgress = false;
      if (zoomRef.current.direction === "in") {
        logger.log("Zoom End");
        map.setOptions({
          ...FreeMapOptions,
          restriction,
        });
        setCityZoomLevel(false);
        setMode("DEFAULT");
      }
    }
  }, [map, updateContext, mode]);

  const onMapDrag = useCallback(() => {
    /* abort fetch listings if map is not ready
     *  or drawing mode or the zoom<9 [cities markers mode]
     * */

    if (!map || mode === "DRAWING" || map?.getZoom() <= 9) return;

    const coords = map?.getBounds()?.toJSON();
    if (!coords) return;

    // If user has drawn a polygon, calculate intersection with visible bounds
    if (userDrawnPolygonRef.current && originalDrawnPolygonRef.current) {
      // Calculate the intersection between the drawn polygon and visible map bounds
      const intersectionPolygon = getPolygonIntersection(
        originalDrawnPolygonRef.current,
        coords,
      );

      if (intersectionPolygon && intersectionPolygon.length >= 3) {
        // Update context with the visible portion of the drawn polygon
        updateContext({
          polygon: intersectionPolygon,
          city: null,
          district: null,
          direction: null,
        });
      } else {
        // If no intersection, keep the original polygon
        updateContext({
          polygon: originalDrawnPolygonRef.current,
          city: null,
          district: null,
          direction: null,
        });
      }
      return;
    }

    // Default behavior: use map bounds as polygon
    const { north, east, south, west } = coords;

    let cityId;
    for (const id of citiesIds) {
      if (citiesPolygon.current[id]?.contains(map.getCenter())) {
        cityId = id;
        break;
      }
    }
    const polygon = [
      { lat: north, lng: east },
      { lat: south, lng: east },
      { lat: south, lng: west },
      {
        lat: north,
        lng: west,
      },
    ];

    updateContext({
      polygon,
      city: null,
      district: null,
      direction: null,
    });
  }, [
    map,
    updateContext,
    mode,
    userDrawnPolygonRef.current,
    originalDrawnPolygonRef.current,
  ]);

  function animateZoomIn() {
    zoomRef.current.direction = "in";
    zoomRef.current.value += 1;
    map.setZoom(zoomRef.current.value);
    map.setCenter(zoomRef.current.center);
    if (zoomRef.current.value >= zoomRef.current.target) {
      zoomRef.current.value = 0;
      return;
    }
    setTimeout(animateZoomIn, 200);
  }

  useEffect(() => {
    function open() {
      trackListingsList(path, listings, { event: "open_listing_list_filters" });
    }
    document.addEventListener("opensearchbar", open);
    return () => {
      document.removeEventListener("opensearchbar", open);
    };
  }, [path, listings]);

  const removeCitiesMarkers = useCallback(() => {
    for (const citiesMarkerElement of citiesMarker) {
      if (!markersDictionary.current[citiesMarkerElement.id]) continue;
      markersDictionary.current[citiesMarkerElement.id]?.setMap(null);
      markersDictionary.current[citiesMarkerElement.id] = null;
    }
  }, [citiesMarker]);
  const onZoom = useCallback(() => {
    if (zoomRef.current.inProgress) return;
    const zoom = map.getZoom();
    if (zoom < 9) {
      /*
       * first remove all listings marker
       * then we draw cities marker
       * */
      setCityZoomLevel(true);

      const ls = previousListings.current.concat(...listings);
      for (const listing of ls) {
        markersDictionary.current[listing]?.setMap(null);
        markersDictionary.current[listing] = null;
      }

      for (const { html, city, id } of citiesMarker) {
        if (markersDictionary.current[id]) continue;
        const marker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position: { lat: city.display_lat, lng: city.display_lng },
          content: html,
        });
        markersDictionary.current[id] = marker;
        marker.addListener("click", () => {
          removeCitiesMarkers();
          map.setCenter({ lat: city.lat, lng: city.lng });
          zoomRef.current.center = { lat: city.lat, lng: city.lng };
          zoomRef.current.inProgress = true;
          zoomRef.current.target = 10;
          zoomRef.current.value = map.getZoom();
          animateZoomIn();
        });
      }

      if (!zoomRef.current.inProgress && zoom !== 6) {
        logger.log("Zooming", map.getZoom());

        map.setOptions(FrozenMapOptions);
        zoomRef.current.inProgress = true;
        zoomRef.current.direction = "out";
        setTimeout(() => {
          map.fitBounds(allCitiesBounds, 0);
          map.setOptions(FrozenMapOptions);
        }, 50);
      }
    }
  }, [map, removeCitiesMarkers, allCitiesBounds, listings]);
  useEffect(
    function TravelToDistrict() {
      if (!map || !bounds) return;

      travelToBounds(bounds);
    },
    [map, bounds],
  );

  const sharedSelectItemEvent = (listing) => {
    trackSelectItem({
      itemListName: category ? itemListName : "all_listings",
      listingType: category?.ga_listing_type || listingType,
      category: (category || categoriesDictionary["0"])?.en,
      cityName: city?.name_en,
      directionName: direction?.name_en,
      districtName: district?.name_en,
      filters: {
        ...filtersRef.current,
        ...getMapCenterAndRadius(map),
      },
      count: total,
      listings: [listing].map((listing, index) =>
        listingToEECItemObject(listing, index, categoriesDictionary),
      ),
    });
  };

  useEffect(() => {
    if (loading || !map || map?.getZoom() <= 9) return;
    if (!listing) Modal.close();
    if (!google.maps.marker?.AdvancedMarkerElement) return;
    const newListingsDictionary = listings.reduce(
      (aac, li) => ({ ...aac, [li.id]: li }),
      {},
    );

    function onClick(event, listing) {
      setCurrentListing(listing);
      event?.domEvent?.srcElement?.classList?.add(css.visted);
      sessionStorage.setItem(listing.id, Date.now() + "");
      document.body.dispatchEvent(
        new CustomEvent("updatefrozenindex", {
          detail: { listing },
          bubbles: true,
        }),
      );
      trackGenericCTAClickEvent("listings_map_view", {
        property_category: category?.en,
        listing_type: listingType,
      });
      if (listing.is_auction) {
        trackAuctionPinClicked({
          source: previousPath || decodeURI(document?.referrer),
          propertyCategory: categoriesDictionary?.[listing?.category]?.ga_property_category,
          listingType: categoriesDictionary?.[listing?.category]?.ga_listing_type,
        })
      }
      sharedSelectItemEvent(listing);
    }

    for (const listing of previousListings.current) {
      if (newListingsDictionary[listing]) continue;
      markersDictionary.current[listing]?.setMap(null);
      markersDictionary.current[listing] = null;
    }
    for (let i = 0; i < listings.length; i++) {
      let listing = listings[i];
      if (markersDictionary.current[listing.id]) continue;

      const visited = sessionStorage.getItem(listing.id);
      const markerDivElement =
        listing?.is_auction ? createHtmlListingMarker({
          className: `${css.sov} ${css.marker} ${css.listing} 
            ${visited ? css.visted : ""}`,
            text: `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M8.04024 12.0766H7.70628L7.67673 11.9956C7.43584 11.3501 6.81817 10.9227 6.12661 10.9242H2.62738C1.93729 10.9242 1.31961 11.3516 1.07875 11.9971L1.04771 12.0781H0.701932C0.314773 12.0781 2.07811e-05 12.392 2.07811e-05 12.7781V13.207C-0.00293755 13.5931 0.310336 13.9085 0.697495 13.9099H0.701932L8.04024 13.9055C8.42738 13.9055 8.74215 13.5916 8.74215 13.2055V12.7767C8.74215 12.3905 8.42738 12.0766 8.04024 12.0766ZM13.555 11.3383L9.13669 6.60772C9.59331 6.18919 10.0041 5.72202 10.3602 5.21507C10.6735 5.35359 11.037 5.31233 11.3119 5.10748C11.7316 4.79506 11.8173 4.20116 11.504 3.78262L8.94902 0.379831C8.74363 0.104248 8.40523 -0.0372218 8.06387 0.00846275C7.54371 0.0762475 7.17872 0.552261 7.24817 1.071C5.51186 1.65164 4.00905 2.77314 2.95987 4.26894C2.64512 4.13042 2.2816 4.17168 2.00822 4.37653C1.58708 4.68896 1.50137 5.28286 1.81612 5.70139L4.36812 9.10418C4.57502 9.38271 4.91782 9.52566 5.26214 9.4785C5.78082 9.40628 6.14285 8.9288 6.07192 8.41007C6.65857 8.21406 7.22454 7.95321 7.75503 7.63489L11.0577 13.2026C11.1242 13.319 11.2055 13.428 11.3001 13.5238C11.9044 14.1457 12.9004 14.1605 13.5225 13.5562C14.1461 12.952 14.1609 11.9602 13.555 11.3383Z" fill="white"/>
</svg>
${categoriesDictionary?.[listing?.category]?.name || path?.category?.name} `
          }) :
        listing?.precise_location_request_status === "approved"
          ? createHtmlListingMarker({
              text: `              <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12 5.94049C12 6.48383 11.8906 7.05433 11.6719 7.652C11.4531 8.24968 11.1344 8.8715 10.7156 9.51746C10.2969 10.1634 9.775 10.8305 9.15 11.5188C8.525 12.207 7.80625 12.9133 6.99375 13.6378C6.85625 13.7585 6.7 13.8491 6.525 13.9094C6.35 13.9698 6.175 14 6 14C5.825 14 5.65 13.9698 5.475 13.9094C5.3 13.8491 5.14375 13.7585 5.00625 13.6378C4.19375 12.9133 3.475 12.207 2.85 11.5188C2.225 10.8305 1.70312 10.1634 1.28437 9.51746C0.865625 8.8715 0.546875 8.24968 0.328125 7.652C0.109375 7.05433 0 6.48383 0 5.94049C0 4.12937 0.603125 2.6865 1.80938 1.6119C3.01562 0.537301 4.4125 0 6 0C7.5875 0 8.98438 0.537301 10.1906 1.6119C11.3969 2.6865 12 4.12937 12 5.94049Z" fill="white"/>
<path d="M5.07194 6.80269L4.46763 6.17489C4.29496 5.99552 4.09353 5.90583 3.86331 5.90583C3.63309 5.90583 3.43165 5.99552 3.25899 6.17489C3.08633 6.35426 3 6.56726 3 6.8139C3 7.06054 3.08633 7.27354 3.25899 7.45291L4.46763 8.73094C4.64029 8.91031 4.84173 9 5.07194 9C5.30216 9 5.5036 8.91031 5.67626 8.73094L8.74101 5.54709C8.91367 5.36771 9 5.15471 9 4.90807C9 4.66144 8.91367 4.44843 8.74101 4.26906C8.56834 4.08969 8.36331 4 8.1259 4C7.88849 4 7.68345 4.08969 7.51079 4.26906L5.07194 6.80269Z" fill="#52B44A"/>
</svg>
${getPrice(listing, path.filters)} `,
              className: `${css.sov} ${css.marker} ${css.listing} 
            ${visited ? css.visted : ""}`,
            })
          : listing.sov_campaign_id
            ? createHtmlListingMarker({
                text: `              <span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
                  />
                </svg>
</span>${getPrice(listing, path.filters)} `,
                className: `${css.sov} ${css.marker} ${css.listing} 
            ${visited ? css.visted : ""}`,
              })
            : createHtmlListingMarker({
                text: listing.published
                  ? getPrice(listing, path.filters)
                  : "طلب تسويق",
                className: `${css.marker} ${css.listing} 
            ${(!listing.published && css.unpublished) || ""}
            ${visited ? css.visted : ""}`,
              });

      markersDictionary.current[listing.id] =
        new google.maps.marker.AdvancedMarkerElement({
          map,
          position: listing.location,
          content: markerDivElement,
          zIndex: listing.sov_campaign_id ? 1 : undefined,
        });

      listing = { ...listing, index: i };
      markersDictionary.current[listing.id].addListener("click", (e) =>
        onClick(e, listing),
      );
    }
    previousListings.current = listings.map((l) => l.id);

    return () => {};
  }, [listings, loading, map, updateContext]);

  useEffect(() => {
    return () => {
      logger.log("Cleanup");
      const keys = Object.keys(markersDictionary.current);
      for (const string of keys) {
        markersDictionary.current[string]?.setMap(null);
        markersDictionary.current[string] = undefined;
      }
    };
  }, []);

  useEffect(() => {
    if (!map) return;

    const listeners = [
      map.addListener("idle", onMapChange),
      map.addListener("dragend", onMapDrag),
      map.addListener("zoom_changed", (e) => {
        // If user has drawn a polygon, calculate intersection with visible bounds
        if (userDrawnPolygonRef.current && originalDrawnPolygonRef.current) {
          const coords = map?.getBounds()?.toJSON();
          if (!coords) return;

          // Calculate the intersection between the drawn polygon and visible map bounds
          const intersectionPolygon = getPolygonIntersection(
            originalDrawnPolygonRef.current,
            coords,
          );

          if (intersectionPolygon && intersectionPolygon.length >= 3) {
            // Update context with the visible portion of the drawn polygon
            updateContext({
              polygon: intersectionPolygon,
              skipRouting: true,
            });
          } else {
            // If no intersection or polygon is too small, keep the original
            updateContext({
              polygon: originalDrawnPolygonRef.current,
              skipRouting: true,
            });
          }
          return;
        }

        // Default behavior: use map bounds as polygon
        const coords = map?.getBounds()?.toJSON();
        if (!coords) return;
        const { north, east, south, west } = coords;

        let cityId;
        for (const id of citiesIds) {
          if (citiesPolygon.current[id]?.contains(map.getCenter())) {
            cityId = id;
            break;
          }
        }
        const polygon = [
          { lat: north, lng: east },
          { lat: south, lng: east },
          { lat: south, lng: west },
          {
            lat: north,
            lng: west,
          },
        ];

        updateContext({
          polygon,
          skipRouting: true,
        });
      }),
    ];

    return () => {
      for (const listener of listeners) {
        listener.remove();
      }
    };
  }, [map, updateContext, mode]);

  useEffect(() => {
    return () => {
      userDrawnPolygonRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("zoom_changed", onZoom);
    return () => listener.remove();
  }, [onZoom, map]);

  return (
    <>
      <Title />
      <div className={css.fullScreen}>
        <div className={css.mapWrapper}>
          <Map
            center={defaultProps.center}
            zoom={defaultProps.zoom}
            onLoad={(map) => {
              map.addListener("click", () => setCurrentListing(undefined));
              setMap(map);
            }}
          />
        </div>

        {mode === "FLOATING_LIST" && (
          <List
            setListing={(listing) => {
              setFullListing(true);
              setCurrentListing(listing);
              sharedSelectItemEvent(listing);
            }}
            listings={listings}
            total={total}
            loading={loading}
            fetchMore={fetchMore}
          />
        )}
        {map && !cityZoomLevel && (
          <MapControls
            mapType={mapType}
            map={map}
            setMapType={setMapType}
            mode={mode}
            setMode={setMode}
            onPolygonDrawn={(isUserDrawn, polygonData) => {
              userDrawnPolygonRef.current = isUserDrawn;
              if (isUserDrawn && polygonData) {
                // Store the original polygon coordinates for intersection calculations
                originalDrawnPolygonRef.current = polygonData;
              } else {
                // Polygon was removed - reset and search within visible map bounds
                originalDrawnPolygonRef.current = null;

                // Trigger a search with current map bounds
                if (map && map.getZoom() > 9) {
                  const coords = map.getBounds()?.toJSON();
                  if (coords) {
                    const { north, east, south, west } = coords;
                    const polygon = [
                      { lat: north, lng: east },
                      { lat: south, lng: east },
                      { lat: south, lng: west },
                      { lat: north, lng: west },
                    ];
                    updateContext({
                      polygon,
                      city: null,
                      district: null,
                      direction: null,
                    });
                  }
                }
              }
            }}
          />
        )}
        {!cityZoomLevel && (
          <div className={css.categories}>
            <CategoryList />
          </div>
        )}

        {!cityZoomLevel && (
          <div className={css.total}>
            {loading ? (
              <Skeleton className={css.loading} />
            ) : total ? (
              <span>
                {total > LISTINGS_SIZE ? LISTINGS_SIZE : total} من {total}{" "}
                إعلان، قرب أكثر لجميع الإعلانات
              </span>
            ) : (
              <span>لا يوجد عقارات هنا. الرجاء تغيير البحث</span>
            )}
          </div>
        )}
        {!fullListing && currentListing && mode !== "FLOATING_LIST" && (
          <div className={css.listingContainer}>
            <ListingCard
              onClick={() => {
                setFullListing(true);

                if(currentListing.is_auction)
                trackMapAuctionListingClicked({
                  source: previousPath || decodeURI(document?.referrer),
                  propertyCategory: categoriesDictionary?.[currentListing?.category]?.ga_property_category,
                  listingType: categoriesDictionary?.[currentListing?.category]?.ga_listing_type,
                })
              }}
              listing={currentListing}
            />
          </div>
        )}
        {fullListing && currentListing && (
          <div ref={listingDetailsRef} className={css.fullListing}>
            <FloatingListingDetails
              onListingChange={(listing) => {
                if (!listing) setFullListing(false);
                setCurrentListing(listing);
                listingDetailsRef.current.scroll(0, 0);
              }}
              frozenContext={path}
              listing={currentListing}
            />
          </div>
        )}
      </div>
    </>
  );
}

const MapPage = WithAuthentication(function MapPage() {
  return <MapScreen />;
});

MapPage.getInitialProps = InitialResolver(
  /* withServerOnly: insures it only run on SSR */

  //AuthResolver(),
  Filter.Resolve,
  Filter.Map.Resolve(
    "/map",
  ) /* Category.Resolve: Resolves if path=/category/page? */,
  Category.Resolve,

  /* City.Resolve: Resolves if path=/category/city/page? */
  City.Resolve,

  /* Direction.Resolve: Resolves if path=/category/city/direction/page? */
  Direction.Resolve,

  /* District.Resolve: Resolves if path=/category/riyadhOrJeddah/direction/district/page?
   * District.Resolve: Resolves if path=/category/city/district/page?
   * */
  District.Resolve,

  /* Listing.Resolve: Resolves if path=/category/riyadhOrJeddah/direction/district/listing
   * Listing.Resolve: Resolves if path=/category/city/district/listing
   * */
  Listing.Resolve,
  (ctx) => {
    return { path: ctx.path, error: { msg: "Not Found" } };
  },
);
MapPage.getLayout = ({ children }) => (
  <div dir="rtl" className={css.root}>
    <div className={css.filters}>
      <Filters.Layout />
    </div>
    <AppLayout hideFooter>
      <div className={`${css.map}`}>
        <div className={css.fullScreen}>{children}</div>
      </div>
    </AppLayout>
  </div>
);
export default MapPage;
